<script setup>
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import Button from 'dashboard/components-next/button/Button.vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t, tm } = useI18n();
const { uiSettings } = useUISettings();

const assistants = useMapGetter('captainAssistants/getRecords');
const heroHighlights = computed(
  () => tm('CAPTAIN.TUTORIALS.HERO.HIGHLIGHTS') || []
);

const quickStartSteps = computed(
  () => tm('CAPTAIN.TUTORIALS.QUICK_START.STEPS') || []
);
const frameworkItems = computed(
  () => tm('CAPTAIN.TUTORIALS.FRAMEWORK.ITEMS') || []
);
const moduleCards = computed(() => tm('CAPTAIN.TUTORIALS.MODULES.ITEMS') || []);
const recipeCards = computed(() => tm('CAPTAIN.TUTORIALS.RECIPES.ITEMS') || []);
const bestPracticesDo = computed(
  () => tm('CAPTAIN.TUTORIALS.BEST_PRACTICES.DO') || []
);
const bestPracticesDont = computed(
  () => tm('CAPTAIN.TUTORIALS.BEST_PRACTICES.DONT') || []
);
const rolloutChecklist = computed(
  () => tm('CAPTAIN.TUTORIALS.ROLL_OUT.CHECKLIST') || []
);

const preferredAssistantId = computed(() =>
  Number(uiSettings.value.last_active_assistant_id)
);

const activeAssistant = computed(() => {
  if (!assistants.value?.length) return null;

  return (
    assistants.value.find(
      assistant => assistant.id === preferredAssistantId.value
    ) || assistants.value[0]
  );
});

const heroStatus = computed(() => {
  if (activeAssistant.value?.name) {
    return t('CAPTAIN.TUTORIALS.HERO.STATUS.WITH_ASSISTANT', {
      assistantName: activeAssistant.value.name,
    });
  }

  return t('CAPTAIN.TUTORIALS.HERO.STATUS.WITHOUT_ASSISTANT');
});

const routeTo = name => {
  router.push({
    name,
    params: { accountId: route.params.accountId },
  });
};

const routeToAssistantPage = name => {
  if (!activeAssistant.value?.id) {
    routeTo('captain_assistants_create_index');
    return;
  }

  router.push({
    name,
    params: {
      accountId: route.params.accountId,
      assistantId: activeAssistant.value.id,
    },
  });
};

const quickActions = computed(() => [
  {
    key: 'createAssistant',
    icon: 'i-lucide-bot',
    label: t('CAPTAIN.TUTORIALS.QUICK_ACTIONS.CREATE_ASSISTANT.LABEL'),
    description: t(
      'CAPTAIN.TUTORIALS.QUICK_ACTIONS.CREATE_ASSISTANT.DESCRIPTION'
    ),
    action: () => routeTo('captain_assistants_create_index'),
  },
  {
    key: 'responses',
    icon: 'i-lucide-message-square-quote',
    label: t('CAPTAIN.TUTORIALS.QUICK_ACTIONS.OPEN_FAQS.LABEL'),
    description: t('CAPTAIN.TUTORIALS.QUICK_ACTIONS.OPEN_FAQS.DESCRIPTION'),
    action: () => routeToAssistantPage('captain_assistants_responses_index'),
  },
  {
    key: 'documents',
    icon: 'i-lucide-file-stack',
    label: t('CAPTAIN.TUTORIALS.QUICK_ACTIONS.OPEN_DOCUMENTS.LABEL'),
    description: t(
      'CAPTAIN.TUTORIALS.QUICK_ACTIONS.OPEN_DOCUMENTS.DESCRIPTION'
    ),
    action: () => routeToAssistantPage('captain_assistants_documents_index'),
  },
  {
    key: 'guardrails',
    icon: 'i-lucide-shield-check',
    label: t('CAPTAIN.TUTORIALS.QUICK_ACTIONS.OPEN_GUARDRAILS.LABEL'),
    description: t(
      'CAPTAIN.TUTORIALS.QUICK_ACTIONS.OPEN_GUARDRAILS.DESCRIPTION'
    ),
    action: () => routeToAssistantPage('captain_assistants_guardrails_index'),
  },
  {
    key: 'guidelines',
    icon: 'i-lucide-scroll-text',
    label: t('CAPTAIN.TUTORIALS.QUICK_ACTIONS.OPEN_GUIDELINES.LABEL'),
    description: t(
      'CAPTAIN.TUTORIALS.QUICK_ACTIONS.OPEN_GUIDELINES.DESCRIPTION'
    ),
    action: () => routeToAssistantPage('captain_assistants_guidelines_index'),
  },
  {
    key: 'scenarios',
    icon: 'i-lucide-spline',
    label: t('CAPTAIN.TUTORIALS.QUICK_ACTIONS.OPEN_SCENARIOS.LABEL'),
    description: t(
      'CAPTAIN.TUTORIALS.QUICK_ACTIONS.OPEN_SCENARIOS.DESCRIPTION'
    ),
    action: () => routeToAssistantPage('captain_assistants_scenarios_index'),
  },
  {
    key: 'playground',
    icon: 'i-lucide-sparkles',
    label: t('CAPTAIN.TUTORIALS.QUICK_ACTIONS.OPEN_PLAYGROUND.LABEL'),
    description: t(
      'CAPTAIN.TUTORIALS.QUICK_ACTIONS.OPEN_PLAYGROUND.DESCRIPTION'
    ),
    action: () => routeToAssistantPage('captain_assistants_playground_index'),
  },
]);

onMounted(() => {
  if (!assistants.value?.length) {
    store.dispatch('captainAssistants/get');
  }
});
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.TUTORIALS.HEADER')"
    :show-pagination-footer="false"
    :show-know-more="false"
    :show-assistant-switcher="false"
    :feature-flag="FEATURE_FLAGS.CAPTAIN"
    class="[&>header>div]:max-w-[80rem] [&>main>div]:max-w-[80rem]"
  >
    <template #body>
      <div class="flex flex-col gap-8 pb-8">
        <section class="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <div
            class="flex flex-col gap-6 rounded-2xl border border-n-strong bg-n-surface-2 p-6"
          >
            <div class="flex flex-col gap-4">
              <p
                class="text-xs font-semibold uppercase tracking-[0.18em] text-n-brand"
              >
                {{ t('CAPTAIN.TUTORIALS.HERO.EYEBROW') }}
              </p>
              <div class="flex flex-col gap-3">
                <h2 class="text-3xl font-semibold text-n-slate-12">
                  {{ t('CAPTAIN.TUTORIALS.HERO.TITLE') }}
                </h2>
                <p class="max-w-3xl text-sm leading-6 text-n-slate-11">
                  {{ t('CAPTAIN.TUTORIALS.HERO.DESCRIPTION') }}
                </p>
              </div>
            </div>

            <div class="grid gap-3 md:grid-cols-3">
              <div
                v-for="highlight in heroHighlights"
                :key="highlight"
                class="rounded-2xl border border-n-strong bg-n-surface-1 p-4 text-sm text-n-slate-12"
              >
                {{ highlight }}
              </div>
            </div>

            <div
              class="flex items-start gap-3 rounded-2xl border border-n-strong bg-n-alpha-2 p-4"
            >
              <span
                class="mt-0.5 inline-flex size-8 items-center justify-center rounded-xl bg-n-brand text-white"
              >
                <span class="i-lucide-info size-4" />
              </span>
              <div class="flex flex-col gap-1">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('CAPTAIN.TUTORIALS.HERO.STATUS.TITLE') }}
                </span>
                <p class="text-sm leading-6 text-n-slate-11">
                  {{ heroStatus }}
                </p>
              </div>
            </div>

            <div class="flex flex-wrap gap-3">
              <Button
                :label="
                  $t('CAPTAIN.TUTORIALS.HERO.PRIMARY_ACTION')
                "
                icon="i-lucide-bot"
                @click="routeTo('captain_assistants_create_index')"
              />
              <Button
                :label="
                  $t('CAPTAIN.TUTORIALS.HERO.SECONDARY_ACTION')
                "
                icon="i-lucide-sparkles"
                color="slate"
                variant="outline"
                @click="
                  routeToAssistantPage('captain_assistants_playground_index')
                "
              />
            </div>
          </div>

          <div
            class="flex flex-col gap-5 rounded-2xl border border-n-strong bg-n-surface-2 p-6"
          >
            <div class="flex flex-col gap-2">
              <h3 class="text-lg font-semibold text-n-slate-12">
                {{ t('CAPTAIN.TUTORIALS.QUICK_START.TITLE') }}
              </h3>
              <p class="text-sm leading-6 text-n-slate-11">
                {{ t('CAPTAIN.TUTORIALS.QUICK_START.DESCRIPTION') }}
              </p>
            </div>

            <div class="flex flex-col gap-4">
              <div
                v-for="(step, index) in quickStartSteps"
                :key="step.title"
                class="flex gap-4 rounded-2xl border border-n-strong bg-n-surface-1 p-4"
              >
                <span
                  class="inline-flex size-9 shrink-0 items-center justify-center rounded-xl bg-n-brand text-sm font-semibold text-white"
                >
                  {{ index + 1 }}
                </span>
                <div class="flex flex-col gap-1">
                  <h4 class="text-sm font-medium text-n-slate-12">
                    {{ step.title }}
                  </h4>
                  <p class="text-sm leading-6 text-n-slate-11">
                    {{ step.description }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
          <div
            class="flex flex-col gap-5 rounded-2xl border border-n-strong bg-n-surface-2 p-6"
          >
            <div class="flex flex-col gap-2">
              <h3 class="text-xl font-semibold text-n-slate-12">
                {{ t('CAPTAIN.TUTORIALS.FRAMEWORK.TITLE') }}
              </h3>
              <p class="text-sm leading-6 text-n-slate-11">
                {{ t('CAPTAIN.TUTORIALS.FRAMEWORK.DESCRIPTION') }}
              </p>
            </div>

            <div class="grid gap-4 md:grid-cols-2">
              <article
                v-for="item in frameworkItems"
                :key="item.title"
                class="flex flex-col gap-3 rounded-2xl border border-n-strong bg-n-surface-1 p-5"
              >
                <div class="flex items-center gap-3">
                  <span
                    class="inline-flex size-10 items-center justify-center rounded-xl bg-n-alpha-2 text-n-slate-12"
                  >
                    <span :class="[item.icon, 'size-5']" />
                  </span>
                  <h4 class="text-base font-semibold text-n-slate-12">
                    {{ item.title }}
                  </h4>
                </div>
                <p class="text-sm leading-6 text-n-slate-11">
                  {{ item.description }}
                </p>
                <div
                  class="rounded-2xl border border-dashed border-n-strong bg-n-surface-2 p-3 text-sm text-n-slate-12"
                >
                  {{ item.example }}
                </div>
              </article>
            </div>
          </div>

          <div
            class="flex flex-col gap-4 rounded-2xl border border-n-strong bg-n-surface-2 p-6"
          >
            <div class="flex flex-col gap-2">
              <h3 class="text-xl font-semibold text-n-slate-12">
                {{ t('CAPTAIN.TUTORIALS.QUICK_ACTIONS.TITLE') }}
              </h3>
              <p class="text-sm leading-6 text-n-slate-11">
                {{ t('CAPTAIN.TUTORIALS.QUICK_ACTIONS.DESCRIPTION') }}
              </p>
            </div>

            <div class="grid gap-3">
              <button
                v-for="action in quickActions"
                :key="action.key"
                type="button"
                class="flex items-start gap-3 rounded-2xl border border-n-strong bg-n-surface-1 p-4 text-left transition hover:bg-n-alpha-2"
                @click="action.action"
              >
                <span
                  class="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-n-alpha-2 text-n-slate-12"
                >
                  <span :class="[action.icon, 'size-5']" />
                </span>
                <span class="flex flex-col gap-1">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ action.label }}
                  </span>
                  <span class="text-sm leading-6 text-n-slate-11">
                    {{ action.description }}
                  </span>
                </span>
              </button>
            </div>
          </div>
        </section>

        <section class="flex flex-col gap-5">
          <div class="flex flex-col gap-2">
            <h3 class="text-xl font-semibold text-n-slate-12">
              {{ t('CAPTAIN.TUTORIALS.MODULES.TITLE') }}
            </h3>
            <p class="text-sm leading-6 text-n-slate-11">
              {{ t('CAPTAIN.TUTORIALS.MODULES.DESCRIPTION') }}
            </p>
          </div>

          <div class="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            <article
              v-for="item in moduleCards"
              :key="item.title"
              class="flex flex-col gap-4 rounded-2xl border border-n-strong bg-n-surface-2 p-5"
            >
              <div class="flex items-center gap-3">
                <span
                  class="inline-flex size-10 items-center justify-center rounded-xl bg-n-alpha-2 text-n-slate-12"
                >
                  <span :class="[item.icon, 'size-5']" />
                </span>
                <h4 class="text-base font-semibold text-n-slate-12">
                  {{ item.title }}
                </h4>
              </div>
              <p class="text-sm leading-6 text-n-slate-11">
                {{ item.description }}
              </p>
              <div class="flex flex-col gap-2 rounded-2xl bg-n-surface-1 p-4">
                <span class="text-xs font-semibold uppercase tracking-wide text-n-slate-10">
                  {{ t('CAPTAIN.TUTORIALS.MODULES.WHEN_TO_USE') }}
                </span>
                <p class="text-sm leading-6 text-n-slate-12">
                  {{ item.whenToUse }}
                </p>
              </div>
              <div class="flex flex-col gap-2 rounded-2xl bg-n-surface-1 p-4">
                <span class="text-xs font-semibold uppercase tracking-wide text-n-slate-10">
                  {{ t('CAPTAIN.TUTORIALS.MODULES.EXAMPLE') }}
                </span>
                <p class="text-sm leading-6 text-n-slate-12">
                  {{ item.example }}
                </p>
              </div>
            </article>
          </div>
        </section>

        <section class="flex flex-col gap-5">
          <div class="flex flex-col gap-2">
            <h3 class="text-xl font-semibold text-n-slate-12">
              {{ t('CAPTAIN.TUTORIALS.RECIPES.TITLE') }}
            </h3>
            <p class="text-sm leading-6 text-n-slate-11">
              {{ t('CAPTAIN.TUTORIALS.RECIPES.DESCRIPTION') }}
            </p>
          </div>

          <div class="grid gap-4 xl:grid-cols-2">
            <article
              v-for="recipe in recipeCards"
              :key="recipe.title"
              class="flex flex-col gap-4 rounded-2xl border border-n-strong bg-n-surface-2 p-5"
            >
              <div class="flex flex-col gap-1">
                <h4 class="text-base font-semibold text-n-slate-12">
                  {{ recipe.title }}
                </h4>
                <p class="text-sm leading-6 text-n-slate-11">
                  {{ recipe.useCase }}
                </p>
              </div>

              <div class="rounded-2xl bg-n-surface-1 p-4">
                <span class="text-xs font-semibold uppercase tracking-wide text-n-slate-10">
                  {{ t('CAPTAIN.TUTORIALS.RECIPES.PROMPT') }}
                </span>
                <p class="mt-2 whitespace-pre-line text-sm leading-6 text-n-slate-12">
                  {{ recipe.prompt }}
                </p>
              </div>

              <div class="rounded-2xl bg-n-surface-1 p-4">
                <span class="text-xs font-semibold uppercase tracking-wide text-n-slate-10">
                  {{ t('CAPTAIN.TUTORIALS.RECIPES.EXPECTED_RESULT') }}
                </span>
                <p class="mt-2 text-sm leading-6 text-n-slate-12">
                  {{ recipe.outcome }}
                </p>
              </div>
            </article>
          </div>
        </section>

        <section class="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]">
          <div
            class="flex flex-col gap-5 rounded-2xl border border-n-strong bg-n-surface-2 p-6"
          >
            <div class="flex flex-col gap-2">
              <h3 class="text-xl font-semibold text-n-slate-12">
                {{ t('CAPTAIN.TUTORIALS.BEST_PRACTICES.TITLE') }}
              </h3>
              <p class="text-sm leading-6 text-n-slate-11">
                {{ t('CAPTAIN.TUTORIALS.BEST_PRACTICES.DESCRIPTION') }}
              </p>
            </div>

            <div class="grid gap-4 md:grid-cols-2">
              <div class="flex flex-col gap-3 rounded-2xl bg-n-surface-1 p-5">
                <h4 class="text-base font-semibold text-n-slate-12">
                  {{ t('CAPTAIN.TUTORIALS.BEST_PRACTICES.DO_TITLE') }}
                </h4>
                <ul class="flex flex-col gap-3">
                  <li
                    v-for="item in bestPracticesDo"
                    :key="item"
                    class="flex gap-3 text-sm leading-6 text-n-slate-12"
                  >
                    <span class="mt-1 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-n-teal-9/10 text-n-teal-11">
                      <span class="i-lucide-check size-3" />
                    </span>
                    <span>{{ item }}</span>
                  </li>
                </ul>
              </div>

              <div class="flex flex-col gap-3 rounded-2xl bg-n-surface-1 p-5">
                <h4 class="text-base font-semibold text-n-slate-12">
                  {{ t('CAPTAIN.TUTORIALS.BEST_PRACTICES.DONT_TITLE') }}
                </h4>
                <ul class="flex flex-col gap-3">
                  <li
                    v-for="item in bestPracticesDont"
                    :key="item"
                    class="flex gap-3 text-sm leading-6 text-n-slate-12"
                  >
                    <span class="mt-1 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-n-ruby-9/10 text-n-ruby-11">
                      <span class="i-lucide-x size-3" />
                    </span>
                    <span>{{ item }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div
            class="flex flex-col gap-5 rounded-2xl border border-n-strong bg-n-surface-2 p-6"
          >
            <div class="flex flex-col gap-2">
              <h3 class="text-xl font-semibold text-n-slate-12">
                {{ t('CAPTAIN.TUTORIALS.ROLL_OUT.TITLE') }}
              </h3>
              <p class="text-sm leading-6 text-n-slate-11">
                {{ t('CAPTAIN.TUTORIALS.ROLL_OUT.DESCRIPTION') }}
              </p>
            </div>

            <ul class="flex flex-col gap-3">
              <li
                v-for="item in rolloutChecklist"
                :key="item"
                class="flex gap-3 rounded-2xl bg-n-surface-1 p-4 text-sm leading-6 text-n-slate-12"
              >
                <span
                  class="mt-1 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-n-brand text-[10px] font-semibold text-white"
                >
                  ✓
                </span>
                <span>{{ item }}</span>
              </li>
            </ul>
          </div>
        </section>
      </div>
    </template>
  </PageLayout>
</template>
